import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import {
  Building2, Plus, Trash2, Shield, AlertTriangle, CheckCircle, Loader2, CreditCard
} from "lucide-react";

interface LicenseEntity {
  id: number;
  name: string;
  industry: string | null;
  status: string;
  createdAt: string;
}

interface LicenseData {
  allowed: boolean;
  reason: string;
  licensesUsed: number;
  licensesTotal: number;
  licenseQuantity: number;
  usedLicenses: number;
  entities: LicenseEntity[];
}

export default function LicensesPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { deleteBusiness } = useBusinesses();
  const [deleteTarget, setDeleteTarget] = useState<LicenseEntity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddLicenseConfirm, setShowAddLicenseConfirm] = useState(false);

  const addLicense = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/add-license");
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.checkoutUrl) {
        toast({ title: "Redirecting to secure checkout..." });
        window.location.href = data.checkoutUrl;
        return;
      }
      if (data.pending) {
        toast({ title: "License purchase processing", description: "Your plan will update momentarily." });
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
          queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
        }, 3000);
        return;
      }
      toast({ title: "License Added", description: data.message || "You can now create a new business." });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
      navigate("/business/create");
    },
    onError: (err: any) => {
      const details = err?.details;
      if (details?.redirectTo) {
        navigate(details.redirectTo);
        return;
      }
      toast({ title: "Failed", description: details?.message || err?.message || "Could not add license", variant: "destructive" });
    },
  });

  const { data: license, isLoading } = useQuery<LicenseData>({
    queryKey: ["/api/billing/licenses"],
    queryFn: async () => {
      const res = await fetch("/api/billing/licenses", { credentials: "include" });
      return res.json();
    },
  });

  // Used = actual entity count (always live, never below truth).
  // Total = max of API-reported total OR live used count, so a stale
  // license_quantity row can't render "3 / 1" — never shows used > total.
  const entities = license?.entities || [];
  const apiUsed = license?.usedLicenses || 0;
  const apiTotal = license?.licenseQuantity || 0;
  const used = Math.max(apiUsed, entities.length);
  const total = Math.max(apiTotal, used, 1);
  const available = Math.max(0, total - used);
  const pct = Math.round((used / Math.max(total, 1)) * 100);
  const atLimit = used >= total;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await apiRequest("DELETE", `/api/businesses/${deleteTarget.id}`);
      toast({ title: "Business deleted", description: `"${deleteTarget.name}" has been removed and the license freed.` });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
    } catch (err: any) {
      toast({ title: "Delete failed", description: err?.message || "Could not delete business.", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 max-w-3xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Licenses & Entities</h1>
          <p className="text-muted-foreground">Manage your business entities and license allocation</p>
        </div>

        {/* At Limit Warning */}
        {atLimit && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>License limit reached</AlertTitle>
            <AlertDescription>
              All {total} license{total !== 1 ? "s" : ""} in use. Purchase more or remove a business to add a new one.
            </AlertDescription>
          </Alert>
        )}

        {/* License Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              License Usage
            </CardTitle>
            <CardDescription>
              Each business entity requires 1 license
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Licenses Used</span>
              <span className="text-2xl font-bold">{used} <span className="text-base font-normal text-muted-foreground">/ {total}</span></span>
            </div>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className={`h-full transition-all ${atLimit ? "bg-red-500" : pct > 60 ? "bg-yellow-500" : "bg-green-500"}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{used} assigned</span>
              <span>{available} available</span>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-4">
            <Button
              variant="outline"
              className="gap-2"
              disabled={addLicense.isPending}
              onClick={() => setShowAddLicenseConfirm(true)}
            >
              {addLicense.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              {addLicense.isPending ? "Adding..." : "Add License ($25/mo)"}
            </Button>
          </CardFooter>
        </Card>

        {/* Entities List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Your Businesses
            </CardTitle>
            <CardDescription>
              {entities.length} business{entities.length !== 1 ? "es" : ""} using {entities.length} license{entities.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {entities.map((entity, i) => (
              <div key={entity.id} className="flex items-center justify-between p-3 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{entity.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {entity.industry || "No industry"} — License #{i + 1}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-green-600 text-xs">Active</Badge>
                  {entities.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(entity)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {/* Available License Slots */}
            {available > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-dashed border-muted-foreground/30">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <Plus className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">{available} license{available !== 1 ? "s" : ""} available</p>
                    <p className="text-xs text-muted-foreground">Add a new business to use this license</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate("/business/create")} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Add Business
                </Button>
              </div>
            )}

            {/* No available slots */}
            {available === 0 && entities.length > 0 && (
              <div className="text-center py-3 text-sm text-muted-foreground">
                All licenses assigned. Purchase more to add another business.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add License Confirmation */}
        <Dialog open={showAddLicenseConfirm} onOpenChange={setShowAddLicenseConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add another business license?</DialogTitle>
              <DialogDescription className="space-y-2">
                <span className="block">This will <strong>increase your subscription by $25/month</strong> and let you create one more business under your account.</span>
                <span className="block text-xs">
                  • Existing businesses: <strong>{used}</strong> &nbsp;•&nbsp; New limit after purchase: <strong>{Math.max(total, used) + 1}</strong>
                </span>
                <span className="block text-xs">
                  If you already have a paid Stripe subscription, the change is prorated and applied immediately.
                  Otherwise, you'll be redirected to a secure Stripe checkout page to complete the purchase.
                </span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddLicenseConfirm(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setShowAddLicenseConfirm(false);
                  addLicense.mutate();
                }}
                disabled={addLicense.isPending}
              >
                {addLicense.isPending ? "Processing..." : "Continue to checkout"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Business</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This will free 1 license but permanently remove all associated data (transactions, invoices, etc.).
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete Business"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
